<template>
  <Dropdown classMenuItems="md:w-[330px] top-[58px]" classItem="px-4 py-2">
    <span
      class="relative lg:h-[32px] lg:w-[32px] lg:bg-gray-500-f7 mr-6 last:mr-0 text-slate-900 lg:dark:bg-slate-900 dark:text-white cursor-pointer rounded-full text-[20px] flex flex-col items-center justify-center"
    >
      <Icon icon="tabler:coffee" width="24" height="24" />

      <span
        v-if="totalPoints > 0"
        class="absolute lg:right-0 lg:top-0 -top-2 -right-2 min-w-[20px] h-4 px-1 bg-success-500 text-[8px] font-semibold flex items-center justify-center rounded-full text-white z-[99]"
      >
        {{ totalPoints }}
      </span>
    </span>

    <template v-slot:menus>
      <div class="flex justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-600">
        <div class="text-sm text-slate-800 dark:text-slate-200 font-medium leading-6">
          Мои бонусы
        </div>

        <button
          type="button"
          class="underline text-xs text-primary-500"
          @click="goToAllBonuses"
        >
          Все
        </button>
      </div>

      <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-600">
        <div class="text-xs text-slate-400">Всего баллов</div>
        <div class="text-2xl font-semibold text-slate-900 dark:text-white">
          {{ totalPoints }}
        </div>
      </div>

      <div class="divide-y divide-slate-100 dark:divide-slate-800">
        <MenuItem
          v-for="bonus in bonuses"
          :key="bonus.id"
          v-slot="{ active }"
        >
          <div
            :class="`${
              active ? 'bg-slate-100 dark:bg-slate-700 dark:bg-opacity-70' : ''
            } block w-full px-4 py-3 text-sm cursor-pointer`"
          >
            <div class="flex justify-between gap-3">
              <div>
                <div class="text-slate-700 dark:text-slate-300">
                  {{ bonusTitle(bonus.type) }}
                </div>

                <div
                  v-if="bonus.tech_card?.book_name"
                  class="text-xs text-slate-500 mt-1 line-clamp-1"
                >
                  {{ bonus.tech_card.book_name }}
                </div>

                <div
                  v-if="bonus.offer?.offer_number"
                  class="text-xs text-slate-500 mt-1 line-clamp-1"
                >
                  КП № {{ bonus.offer.offer_number }}
                </div>

                <div class="text-xs text-slate-400 mt-1">
                  {{ formatDate(bonus.created_at) }}
                </div>
              </div>

              <div class="text-success-500 font-semibold whitespace-nowrap">
                +{{ bonus.points }}
              </div>
            </div>
          </div>
        </MenuItem>

        <div
          v-if="bonuses.length === 0"
          class="px-4 py-4 text-sm text-slate-400"
        >
          Пока бонусов нет
        </div>
      </div>
    </template>
  </Dropdown>
</template>

<script>
import Dropdown from "@/components/Dropdown";
import Icon from "@/components/Icon";
import { MenuItem } from "@headlessui/vue";
import axios from "axios";

const ROLE_PROJECT_MANAGER = 4;
const ROLE_AUTHOR = 9;
const ROLE_TYPOGRAPHY = 11;
const ROLE_SALES_DEPARTMENT = 21;

const API_BASE = "https://apidash.smartpress.kz/api";

export default {
  name: "Bonus",

  components: {
    Dropdown,
    Icon,
    MenuItem,
  },

  data() {
    return {
      totalPoints: 0,
      bonuses: [],
      activeUser: JSON.parse(localStorage.getItem("activeUser")) || {},
    };
  },

  computed: {
    userRole() {
      return Number(this.activeUser?.role_id || 0);
    },

    bonusSummaryUrl() {
      if (this.userRole === ROLE_PROJECT_MANAGER) {
        return `${API_BASE}/pm-bonuses/summary`;
      }

      if (this.userRole === ROLE_AUTHOR) {
        return `${API_BASE}/author-bonuses/summary`;
      }

      if (this.userRole === ROLE_TYPOGRAPHY) {
        return `${API_BASE}/typography-bonuses/summary`;
      }
      if (this.userRole === ROLE_SALES_DEPARTMENT) {
        return `${API_BASE}/accounting-bonuses/summary`;
      }

      return `${API_BASE}/executor-bonuses/summary`;
    },

    bonusRouteName() {
      if (this.userRole === ROLE_PROJECT_MANAGER) {
        return "project-manager-bonuses";
      }

      if (this.userRole === ROLE_AUTHOR) {
        return "author-bonuses";
      }

      if (this.userRole === ROLE_TYPOGRAPHY) {
        return "typography-bonuses";
      }
       if (this.userRole === ROLE_SALES_DEPARTMENT) {
        return "sales-bonuses";
      }

      return "executor-bonuses";
    },
  },

  mounted() {
    this.fetchBonuses();
  },

  methods: {
    async fetchBonuses() {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(this.bonusSummaryUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        this.totalPoints = response.data.total_points || 0;
        this.bonuses = response.data.bonuses || [];
      } catch (error) {
        console.error("Failed to fetch bonuses:", error);
      }
    },

    goToAllBonuses() {
      this.$router.push({
        name: this.bonusRouteName,
      });
    },

    bonusTitle(type) {
      const map = {
        file_uploaded: "Загрузка файла",
        task_completed: "Завершение задачи",
        high_score: "Высокая оценка",
        profile_completed: "Заполнение профиля",

        moved_to_account: "Передача проекта в ОП",
        task_rated: "Оценка работы исполнителя",
        pm_high_score: "Высокая оценка проекта",
        pm_profile_completed: "Заполнение профиля ПМ",

        work_rated: "Оценка работы исполнителя",
        project_rated: "Оценка проекта",
        application_created: "Создание заявки",

        accepted_to_print: "Принятие в печать",
        print_data_updated: "Изменение печатных параметров",
        moved_to_wap: "Передача в ППР",
        moved_to_stock: "Передача на склад",
        order_status_updated: "Обновление статуса заказа",
        signal_copy_completed: "Завершение сигнальной копии",
        print_started: "Начало печати",
        attendance_checkin: "Приход в офис",
        attendance_checkout: "Завершение рабочего дня",

         /*
        |--------------------------------------------------------------------------
        | ОП
        |--------------------------------------------------------------------------
        */
        kp_sent: "Отправка КП",
        offer_accepted: "Принятие КП",
        invoice_sent: "Отправка счета и договора",

        sales_typo_finish: "Завершение типографии",
        sales_done: "Завершение проекта",

        sales_moved_to_project: "Передача в проект",
        sales_moved_to_stock: "Передача на склад",
        sales_moved_to_archive: "Передача в архив",
        sales_moved_to_account: "Передача в завершенные",

        sales_metadata_imported: "Загрузка метаданных",
        sales_offer_flag_changed: "Изменение флагов КП",

        invoice_created: "Создание счета",
        payment_confirmed: "Подтверждение оплаты",
        moved_to_production: "Передача в производство",
        esf_created: "Создание ЭСФ",
        act_created: "Создание акта",
        moved_to_typography: "Передача в типографию",
        project_completed: "Завершение проекта",

        attendance_checkin: "Приход в офис",
        attendance_checkout: "Завершение рабочего дня",

        sales_book_published: "Публикация книги",
        sales_contract_created: "Создание договора",

      };

      return map[type] || "Бонус";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("ru-RU");
    },
  },
};
</script>